import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  useTheme,
} from '@mui/material';
import { CheckCircle, Euro, OpenInNew, TrendingUp as TrendingUpIcon, Assessment } from '@mui/icons-material';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { BackToTop } from './BackToTop';
import { trackDonation } from '../../configuration/analytics';

const contentUseCase = new ContentUseCase();

export const MEInfoPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { meInfo, donationInfo } = contentUseCase.getAllContent(language);

  const getColorForScore = (score: number) => {
    if (score <= 0.5) return theme.palette.error.main;
    if (score <= 0.7) return theme.palette.warning.main;
    if (score <= 0.8) return theme.palette.primary.main;
    return theme.palette.success.main;
  };

  const getColorForFunding = (funding: number) => {
    if (funding <= 100) return theme.palette.error.main;      // Red for very low funding
    if (funding <= 300) return theme.palette.warning.main;    // Orange for low funding
    if (funding <= 800) return theme.palette.primary.main;    // Blue for moderate funding
    return theme.palette.success.main;                        // Green for high funding
  };

  const handleDonationClick = () => {
    trackDonation(undefined, 'me_info_page');
  };

  return (
    <Container maxWidth="xl" sx={{ py: theme.spacing(6) }}>
      <SEO page="meInfo" />
      <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ mb: theme.spacing(6) }}>
        {meInfo.title}{' '}
        <Box
          component="img"
          src="/Light_blue_ribbon.svg.png"
          alt="Light Blue Awareness Ribbon"
          sx={{
            height: '60px',
            width: 'auto',
            marginLeft: theme.spacing(2),
            verticalAlign: 'middle',
          }}
        />
      </Typography>

      {/* Neuromunological Disease Info */}
      <Box sx={{ mb: theme.spacing(8) }}>
        {meInfo.description.split('\n').map((paragraph, index) => (
          <Typography
            key={`paragraph-${paragraph.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`}
            variant={index === 0 ? "h6" : "body1"}
            color="text.secondary"
            paragraph
            align="center"
            sx={{
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
              mb: index === 0 ? theme.spacing(4) : (index < meInfo.description.split('\n').length - 1 ? theme.spacing(3) : 0),
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Key Facts Section */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.keyFacts.title}
        </Typography>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: theme.spacing(4),
          justifyContent: 'center',
          maxWidth: { xs: '100%', md: 1000 },
          mx: 'auto',
        }}>
          {meInfo.keyFacts.cards.map((card, index) => (
            <Card key={`keyfact-${card.title.toLowerCase().replace(/\s+/g, '-')}`} sx={{ flex: { md: '1 1 calc(33.333% - 16px)' }, textAlign: 'center', minWidth: { md: 300 } }}>
              <CardContent sx={{ p: theme.spacing(4) }}>
                <Box sx={{
                  width: theme.spacing(8),
                  height: theme.spacing(8),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: theme.spacing(2),
                  color: 'primary.main',
                  fontSize: theme.spacing(4),
                  fontWeight: 'bold',
                }}>
                  {card.icon === 'People' && '👥'}
                  {card.icon === 'Virus' && '🦠'}
                  {card.icon === 'Health' && '🏥'}
                </Box>
                <Typography variant="h5" component="h3" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                  {card.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Why This Matters */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.whyThisMatters.title}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          {meInfo.whyThisMatters.description}
        </Typography>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Symptoms Section */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.symptoms.title}
        </Typography>

        {/* PEM - Cardinal Symptom */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h5" color="primary.main" sx={{ mb: theme.spacing(3), fontWeight: 600, textAlign: 'center' }}>
            {meInfo.symptoms.pemTitle}
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ textAlign: 'center', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {meInfo.pemDescription}
          </Typography>
        </Box>

        {/* Other Symptoms */}
        <Typography variant="h4" component="h3" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.symptoms.otherSymptomsTitle}
        </Typography>
        <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          <CardContent sx={{ p: theme.spacing(4) }}>
            <List>
              {meInfo.symptoms.list.map((symptom, index) => (
                <ListItem key={`symptom-${symptom.toLowerCase().replace(/\s+/g, '-')}`} sx={{ py: theme.spacing(2) }}>
                  <ListItemIcon>
                    <CheckCircle color="primary" sx={{ fontSize: theme.spacing(3.5) }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={symptom}
                    primaryTypographyProps={{
                      variant: 'h6',
                      sx: { fontWeight: 500 },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Long COVID Section */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.longCovid.title}
        </Typography>
        <Typography variant="h6" paragraph align="center" sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          {meInfo.longCovid.description}
        </Typography>
        <Card sx={{
          maxWidth: { xs: '100%', md: 1000 },
          mx: 'auto',
          mb: theme.spacing(4),
          backgroundColor: 'primary.light',
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: 2,
          boxShadow: 3,
        }}>
          <CardContent sx={{ p: theme.spacing(3) }}>
            <Typography variant="h6" align="center" sx={{
              color: 'primary.contrastText',
              fontStyle: 'italic',
              fontWeight: 500,
              mb: 0,
            }}>
              {meInfo.longCovid.statement}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          <CardContent sx={{ p: theme.spacing(4) }}>
            <List>
              {meInfo.longCovid.keyPoints.map((point, index) => (
                <ListItem key={`longcovid-point-${point.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`} sx={{ py: theme.spacing(1) }}>
                  <ListItemIcon>
                    <CheckCircle color="primary" sx={{ fontSize: theme.spacing(3) }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={point}
                    primaryTypographyProps={{
                      variant: 'body1',
                      sx: { fontWeight: 500 },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Most Disabling Illness Section */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.mostDisabling.title}
        </Typography>
        <Typography variant="h5" paragraph align="center" sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', fontSize: '1.2rem' }}>
          {meInfo.mostDisabling.description}
        </Typography>

        <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: theme.spacing(4) }}>
              <Assessment sx={{ mr: theme.spacing(2), color: 'error.main', fontSize: theme.spacing(4) }} />
              <Typography variant="h4" component="h3" align="center">
                {meInfo.mostDisabling.study.title}
              </Typography>
            </Box>

            <Typography variant="body1" paragraph sx={{ mb: theme.spacing(4), color: 'text.secondary', textAlign: 'center' }}>
              {meInfo.mostDisabling.study.explanation}
            </Typography>

            <Box sx={{ mb: theme.spacing(3), maxWidth: { xs: '100%', md: 800 }, mx: 'auto' }}>
              {meInfo.mostDisabling.study.data.map((item: any, index: number) => (
                <Box key={`quality-${item.condition.toLowerCase().replace(/\s+/g, '-')}`} sx={{ mb: theme.spacing(3) }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {item.condition}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {item.score.toFixed(2)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.score * 100}
                    sx={{
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getColorForScore(item.score),
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>

            <Button
              variant="outlined"
              href={meInfo.mostDisabling.study.source}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNew />}
              sx={{ mt: theme.spacing(2) }}
            >
              {meInfo.mostDisabling.study.buttonText}
            </Button>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Prevalence Section */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.prevalence.title}
        </Typography>
        <Typography variant="h6" paragraph align="center" sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          {meInfo.prevalence.description}
        </Typography>

        <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom align="center" sx={{ mb: theme.spacing(3) }}>
              {meInfo.prevalence.globalStats.title}
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: { xs: '100%', md: 800 }, mx: 'auto', mb: theme.spacing(3) }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Region</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cases</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Population %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meInfo.prevalence.globalStats.data.map((item: any, index: number) => (
                    <TableRow key={`global-${item.region.toLowerCase().replace(/\s+/g, '-')}`}>
                      <TableCell>{item.region}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.cases}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.percentage}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: theme.spacing(1), alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: theme.spacing(1) }}>
                Sources:
              </Typography>
              {meInfo.prevalence.sources.map((source, index) => (
                <Button
                  key={`source-${source.name.toLowerCase().replace(/\s+/g, '-')}`}
                  variant="outlined"
                  size="small"
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNew />}
                  sx={{ minWidth: 120 }}
                >
                  {source.name}
                </Button>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Underdiagnosis Section */}
        <Box sx={{ mt: theme.spacing(6), textAlign: 'center' }}>
          <Typography variant="h5" component="h3" gutterBottom sx={{ mb: theme.spacing(3), color: 'primary.main' }}>
            {meInfo.prevalence.underdiagnosis.title}
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: { xs: '100%', md: 800 }, mx: 'auto', color: 'text.secondary' }}>
            {meInfo.prevalence.underdiagnosis.description}
          </Typography>
        </Box>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* What Does Living with M.E. Mean */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.livingWithME.title}
        </Typography>
        <Typography variant="h6" paragraph align="center" sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          {meInfo.livingWithME.description}
        </Typography>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: theme.spacing(3),
          flexWrap: 'wrap',
          justifyContent: 'center',
          maxWidth: { xs: '100%', md: 1000 },
          mx: 'auto',
        }}>
          {meInfo.livingWithME.stages.map((stage, index) => (
            <Card key={`stage-${stage.severity.toLowerCase().replace(/\s+/g, '-')}`} sx={{ flex: { md: '1 1 calc(50% - 12px)' }, minWidth: { md: 300 } }}>
              <CardContent sx={{ p: theme.spacing(4), textAlign: 'center' }}>
                <Typography variant="h4" component="h3" gutterBottom color="primary" sx={{ fontWeight: 700 }}>
                  {stage.severity} M.E.
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stage.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Economic Impact Section */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          Economic Impact in Germany
        </Typography>
        <Typography variant="h6" paragraph align="center" sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          {meInfo.economicImpact.description}
        </Typography>

        <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: theme.spacing(4) }}>
              <Euro sx={{ mr: theme.spacing(2), color: 'warning.main', fontSize: theme.spacing(4) }} />
              <Typography variant="h4" component="h3" align="center">
                {meInfo.economicImpact.study.title}
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: { xs: '100%', md: 800 }, mx: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Year</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cost</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>GDP Impact %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meInfo.economicImpact.study.data.map((item: any, index: number) => (
                    <TableRow key={`economic-${item.year.toLowerCase().replace(/\s+/g, '-')}`}>
                      <TableCell>{item.year}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.cost}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{item.gdpImpact}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="outlined"
              href={meInfo.economicImpact.study.source}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNew />}
              sx={{ mt: theme.spacing(3) }}
            >
              {meInfo.economicImpact.study.buttonText}
            </Button>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Research Funding Section */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.researchFunding.title}
        </Typography>
        <Typography variant="h6" paragraph align="center" sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          {meInfo.researchFunding.description}
        </Typography>

        <Card sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: theme.spacing(4) }}>
              <TrendingUpIcon sx={{ mr: theme.spacing(2), color: 'primary.main', fontSize: theme.spacing(4) }} />
              <Typography variant="h4" component="h3" align="center">
                {meInfo.researchFunding.study.title}
              </Typography>
            </Box>

            <Box sx={{ mb: theme.spacing(3), maxWidth: { xs: '100%', md: 800 }, mx: 'auto' }}>
              {meInfo.researchFunding.study.data.map((item: any, index: number) => {
                let fundingInMillions = item.funding;
                if (item.unit.includes('billion')) {
                  fundingInMillions = item.funding * 1000;
                }

                return (
                  <Box key={`funding-${item.condition.toLowerCase().replace(/\s+/g, '-')}`} sx={{ mb: theme.spacing(3) }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {item.condition}
                      </Typography>
                      <Typography variant="h6" color="text.secondary">
                        {item.funding} {item.unit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((fundingInMillions / 2000) * 100, 100)}
                      sx={{
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getColorForFunding(fundingInMillions),
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>

            <Button
              variant="outlined"
              href={meInfo.researchFunding.study.source}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNew />}
              sx={{ mt: theme.spacing(2) }}
            >
              {meInfo.researchFunding.study.buttonText}
            </Button>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Impact on Patients - Enhanced */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {meInfo.impactOnPatients.title}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', mb: theme.spacing(4), color: 'text.primary' }}>
          {meInfo.impactOnPatients.description}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto', mb: theme.spacing(4), color: 'text.primary' }}>
          {meInfo.impactOnPatients.additionalInfo}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
          {meInfo.impactOnPatients.familyImpact}
        </Typography>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Donation Call to Action */}
      <Box sx={{ mb: theme.spacing(8), textAlign: 'center' }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {language === 'de' ? 'Unterstütze die M.E. Forschung' : 'Support M.E. Research'}
        </Typography>
        <Typography variant="h6" paragraph align="center" sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 800 }, mx: 'auto' }}>
          {language === 'de'
            ? 'Hilf uns dabei, das Bewusstsein für M.E. zu schärfen und die Forschung zu unterstützen. Jede Spende macht einen Unterschied im Leben von M.E.-Patienten.'
            : 'Help us raise awareness for M.E. and support research. Every donation makes a difference in the lives of M.E. patients.'
          }
        </Typography>

        <Button
          variant="contained"
          size="large"
          href={donationInfo.mainDonationButton.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleDonationClick}
          sx={{
            px: theme.spacing(6),
            py: theme.spacing(2),
            fontSize: '1.1rem',
            fontWeight: 600,
            minWidth: 250,
          }}
        >
          {donationInfo.mainDonationButton.title}
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mt: theme.spacing(3), fontStyle: 'italic' }}>
          {language === 'de'
            ? 'Deine Spende unterstützt direkt die M.E. Forschung und Patientenversorgungsprogramme'
            : 'Your donation directly supports M.E. research and patient support programs'
          }
        </Typography>
      </Box>
      <BackToTop />
    </Container>
  );
};
